import React, { useEffect, useState } from 'react';
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from '../../../../firebaseConfig';
import { Table, Button, message, Space, Modal } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import PhotoModal from '../../modal/PhotoModal';
import AddItemModal from '../../modal/AddItemModal';
import EditItemModal from '../../modal/EditItemModal';

import './Items.less';
import { useIntl } from 'react-intl';

const Items = () => {
    const intl = useIntl();
    const [items, setItems] = useState<
        { id: string, name: { en: string; zh: string }, shortDesc: { en: string; zh: string }, longDesc: { en: string; zh: string }, price: string }[]
    >([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [editingItem, setEditingItem] = useState<
        { id: string, name: { en: string; zh: string }, shortDesc: { en: string; zh: string }, longDesc: { en: string; zh: string }, price: string } | null
    >(null);

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "items"));
            const newData = querySnapshot.docs.map((doc) => {
                const data = doc.data();

                return {
                    id: doc.id,
                    name: {
                        en: data.name.en || "",
                        zh: data.name['zh-CN'] || "",
                    },
                    shortDesc: {
                        en: data.shortDesc.en || "",
                        zh: data.shortDesc['zh-CN'] || "",
                    },
                    longDesc: {
                        en: data.longDesc.en || "",
                        zh: data.longDesc['zh-CN'] || "",
                    },
                    price: data.price || "",
                    beforeDiscountPrice: data.beforeDiscountPrice || "",
                };
            });
            newData.sort((a, b) => a.name.en.localeCompare(b.name.en));
            setItems(newData);
        } catch (error) {
            console.error("Error fetching documents: ", error);
        }
    };

    const handleDeleteItem = async (id: string) => {
        Modal.confirm({
            title: intl.formatMessage({ id: "DELETE_CONFIRM" }),
            okText: intl.formatMessage({ id: "YES" }),
            cancelText: intl.formatMessage({ id: "NO" }),
            onOk: async () => {
                try {
                    const itemDoc = doc(db, "items", id);
                    await deleteDoc(itemDoc);
                    fetchItems();
                    message.success(intl.formatMessage({ id: "DELETE_SUCCESS" }));
                } catch (error) {
                    console.error("Error deleting document: ", error);
                    message.error(intl.formatMessage({ id: "DELETE_ERROR" }));
                }
            },
        });
    };

    const handleOpenPhotoModal = (itemId: string) => {
        setSelectedItemId(itemId);
        setIsPhotoModalOpen(true);
    };

    const columns = [
        {
            title: intl.formatMessage({ id: "NAME" }),
            dataIndex: 'name',
            key: 'name',
            render: (name: { en: string; zh: string }) => (
                <>
                    <div>{`EN: ${name.en}`}</div>
                    <div>{`ZH: ${name.zh}`}</div>
                </>
            ),
        },
        {
            title: intl.formatMessage({ id: "SHORT_DESC" }),
            dataIndex: 'shortDesc',
            key: 'shortDesc',
            render: (shortDesc: { en: string; zh: string }) => (
                <>
                    <div>{`EN: ${shortDesc.en}`}</div>
                    <div>{`ZH: ${shortDesc.zh}`}</div>
                </>
            ),
        },
        {
            title: intl.formatMessage({ id: "LONG_DESC" }),
            dataIndex: 'longDesc',
            key: 'longDesc',
            render: (longDesc: { en: string; zh: string }) => (
                <>
                    <div>{`EN: ${longDesc.en}`}</div>
                    <div>{`ZH: ${longDesc.zh}`}</div>
                </>
            ),
        },
        { title: intl.formatMessage({ id: "PRICE" }), dataIndex: 'price', key: 'price' },
        { title: intl.formatMessage({ id: "DISCOUNTED_PRICE" }), dataIndex: 'beforeDiscountPrice', key: 'beforeDiscountPrice' },
        {
            title: intl.formatMessage({ id: "PHOTOS" }),
            key: 'photos',
            render: (text: any, record: any) => (
                <Button onClick={() => handleOpenPhotoModal(record.id)}>
                    {intl.formatMessage({ id: "MANAGE_PHOTOS" })}
                </Button>
            ),
        },
        {
            title: intl.formatMessage({ id: "ACTIONS" }),
            key: 'actions',
            render: (text: any, record: any) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingItem(record);
                            setIsEditModalOpen(true);
                        }}
                    />
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteItem(record.id)}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div className="items-container">
            <div className='items-list-header'>
                <h2>{intl.formatMessage({ id: "ITEMS_LIST" })}</h2>
                <Button type="primary" onClick={() => setIsAddModalOpen(true)}>
                    {intl.formatMessage({ id: "ADD_NEW_ITEM" })}
                </Button>
            </div>
            <br />
            <div className='items-table'>
                <Table bordered={true} dataSource={items} columns={columns} rowKey="id" />
            </div>

            <AddItemModal
                isVisible={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                fetchItems={fetchItems}
            />

            {editingItem && (
                <EditItemModal
                    isVisible={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setEditingItem(null);
                    }}
                    fetchItems={fetchItems}
                    editingItem={editingItem}
                    setEditingItem={setEditingItem}
                />
            )}

            {selectedItemId && (
                <PhotoModal
                    isOpen={isPhotoModalOpen}
                    onClose={() => setIsPhotoModalOpen(false)}
                    itemId={selectedItemId}
                />
            )}
        </div>
    );
};

export default Items;
